import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileSpreadsheet, Search, ExternalLink, Columns3, Save, Trash2, Users, Database } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { RagBadge } from "@/components/Rag";
import { StatusPill } from "@/components/LogSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const RAG_KEYS = ["rag", "overall_rag", "previous_overall_rag", "plan_rag", "resource_rag", "finance_rag", "benefits_rag", "vendor_rag"];
const PILL_KEYS = ["status", "approval_status", "criticality", "priority", "category", "rating", "approval_level"];
const header = (c) => c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()).replace(/\bRag\b/g, "RAG").replace(/^Ref$/, "ID");

export default function Controls() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [filters, setFilters] = useState({ portfolio_id: "", programme_id: "" });
  const [q, setQ] = useState("");
  const [groupBy, setGroupBy] = useState("");
  const [visible, setVisible] = useState(null);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: "", shared: false, bi_enabled: false });
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    api.get("/portfolios").then(({ data }) => setPortfolios(data));
    api.get("/programmes").then(({ data }) => setProgrammes(data));
  }, []);

  const loadViews = useCallback(() => {
    api.get(`/control-views?kind=${kind}`).then(({ data }) => setViews(data));
  }, [kind]);

  useEffect(() => { loadViews(); }, [loadViews]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.portfolio_id) p.set("portfolio_id", filters.portfolio_id);
    if (filters.programme_id) p.set("programme_id", filters.programme_id);
    if (q) p.set("q", q);
    return p.toString();
  }, [filters, q]);

  useEffect(() => {
    setData(null);
    const qs = params();
    api
      .get(`/controls/${kind}${qs ? `?${qs}` : ""}`)
      .then(({ data }) => {
        setData(data);
        setVisible((v) => (v && v.every((c) => data.columns.includes(c)) ? v : data.columns));
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [kind, params]);

  useEffect(() => {
    setGroupBy("");
    setVisible(null);
    setActiveView("");
  }, [kind]);

  const applyView = (vid) => {
    setActiveView(vid);
    const v = views.find((x) => x.id === vid);
    if (!v) {
      if (data) setVisible(data.columns);
      setGroupBy("");
      setFilters({ portfolio_id: "", programme_id: "" });
      return;
    }
    setVisible(v.columns?.length ? v.columns : data?.columns || []);
    setGroupBy(v.group_by || "");
    setFilters({ portfolio_id: v.portfolio_id || "", programme_id: v.programme_id || "" });
  };

  const saveView = async () => {
    if (!saveForm.name.trim()) return toast.error("Give the view a name");
    try {
      await api.post("/control-views", {
        name: saveForm.name.trim(),
        kind,
        columns: visible || [],
        group_by: groupBy || null,
        portfolio_id: filters.portfolio_id || null,
        programme_id: filters.programme_id || null,
        shared: saveForm.shared,
        bi_enabled: saveForm.bi_enabled,
      });
      toast.success(saveForm.bi_enabled ? "View saved and published to BI" : saveForm.shared ? "View saved and shared with the workspace" : "View saved");
      setSaveOpen(false);
      setSaveForm({ name: "", shared: false, bi_enabled: false });
      loadViews();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deleteView = async () => {
    if (!activeView) return;
    const v = views.find((x) => x.id === activeView);
    if (!window.confirm(`Delete the view "${v?.name}"?`)) return;
    try {
      await api.delete(`/control-views/${activeView}`);
      toast.success("View deleted");
      setActiveView("");
      if (data) setVisible(data.columns);
      loadViews();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const exportExcel = async () => {
    try {
      const qs = params();
      const res = await api.get(`/controls/${kind}/export${qs ? `?${qs}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-all-projects.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported to Excel");
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  };

  const renderCell = (row, c) => {
    const v = row[c];
    if (RAG_KEYS.includes(c) && v) return <RagBadge value={v} />;
    if (PILL_KEYS.includes(c) && v) return <StatusPill value={v} />;
    if (v === true) return "Yes";
    if (v === false) return "No";
    if (c === "project_name") {
      return (
        <button
          data-testid={`control-open-${row.id}`}
          onClick={() => row.project_id && navigate(`/projects/${row.project_id}`)}
          className="inline-flex items-center gap-1 font-medium text-slate-800 underline-offset-2 hover:underline"
        >
          {v} <ExternalLink size={12} className="text-slate-400" />
        </button>
      );
    }
    return v === null || v === undefined || v === "" ? "—" : String(v);
  };

  const columns = (visible?.length ? visible : data?.columns) || [];
  const groupOptions = ["portfolio_name", "programme_name", "project_name", "status", "rag", "priority", "criticality", "category", "approval_status"]
    .filter((g) => data?.columns?.includes(g));

  const groups = data
    ? groupBy
      ? Object.entries(
          data.rows.reduce((acc, r) => {
            const k = r[groupBy] || "Unassigned";
            (acc[k] = acc[k] || []).push(r);
            return acc;
          }, {}),
        )
      : [[null, data.rows]]
    : [];

  return (
    <Shell
      testId="controls-page"
      title={data?.label || "Project Controls"}
      subtitle={data ? `${data.count} record${data.count === 1 ? "" : "s"} across all projects` : "Loading…"}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="controls-columns-btn" onClick={() => setPickerOpen(true)} className="gap-1.5">
            <Columns3 size={15} /> Columns
          </Button>
          <Button variant="outline" data-testid="controls-save-view-btn" onClick={() => setSaveOpen(true)} className="gap-1.5">
            <Save size={15} /> Save View
          </Button>
          <Button variant="outline" data-testid="controls-export-btn" onClick={exportExcel} className="gap-1.5">
            <FileSpreadsheet size={15} /> Export to Excel
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative w-full max-w-xs">
          <label className="field-label">Quick find</label>
          <Search size={15} className="absolute left-2.5 top-[30px] text-slate-400" />
          <Input data-testid="controls-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all records…" className="pl-8" />
        </div>
        <div className="w-56">
          <label className="field-label">Saved View</label>
          <div className="flex gap-1.5">
            <Select
              testId="controls-view-select"
              value={activeView}
              onChange={applyView}
              options={views.map((v) => ({ value: v.id, label: `${v.name}${v.shared ? " (shared)" : ""}${v.bi_enabled ? " · BI" : ""}` }))}
              placeholder="Default view"
            />
            {activeView && (
              <button data-testid="controls-delete-view-btn" onClick={deleteView} aria-label="Delete view" title="Delete view" className="shrink-0 px-1 text-slate-400 hover:text-red-600">
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
        <div className="w-48">
          <label className="field-label">Group By</label>
          <Select testId="controls-groupby-select" value={groupBy} onChange={setGroupBy} options={groupOptions.map((g) => ({ value: g, label: header(g) }))} placeholder="(no grouping)" />
        </div>
        <div className="w-48">
          <label className="field-label">Portfolio</label>
          <Select testId="controls-portfolio-filter" value={filters.portfolio_id} onChange={(v) => setFilters((f) => ({ ...f, portfolio_id: v }))} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="All portfolios" />
        </div>
        <div className="w-48">
          <label className="field-label">Programme</label>
          <Select testId="controls-programme-filter" value={filters.programme_id} onChange={(v) => setFilters((f) => ({ ...f, programme_id: v }))} options={programmes.map((p) => ({ value: p.id, label: p.name }))} placeholder="All programmes" />
        </div>
      </div>

      {!data ? (
        <Empty text="Loading records…" />
      ) : data.rows.length === 0 ? (
        <Empty testId="controls-empty" text="No records match this view." />
      ) : (
        <div className="space-y-5">
          {groups.map(([groupName, rows]) => (
            <div key={groupName || "all"} className="panel overflow-x-auto" data-testid={`controls-group-${(groupName || "all").replace(/\s+/g, "-").toLowerCase()}`}>
              {groupName && (
                <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600">
                  {groupName} <span className="ml-1 font-normal text-slate-400">({rows.length})</span>
                </div>
              )}
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{columns.map((c) => <th key={c} className="px-4 py-2.5 font-semibold">{header(c)}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`control-row-${r.id}`} className="transition-colors hover:bg-slate-50">
                      {columns.map((c) => (
                        <td key={c} className="px-4 py-2.5 text-slate-600">{renderCell(r, c)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent data-testid="controls-columns-dialog" className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Columns</DialogTitle>
            <DialogDescription>Choose which columns appear on this register. Save it as a view to keep it.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(data?.columns || []).map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  data-testid={`controls-column-${c}`}
                  type="checkbox"
                  className="h-4 w-4"
                  checked={columns.includes(c)}
                  onChange={(e) =>
                    setVisible((v) => {
                      const base = v?.length ? v : data.columns;
                      return e.target.checked
                        ? data.columns.filter((x) => base.includes(x) || x === c)
                        : base.filter((x) => x !== c);
                    })
                  }
                />
                {header(c)}
              </label>
            ))}
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" data-testid="controls-columns-reset-btn" onClick={() => setVisible(data?.columns || [])}>Reset</Button>
            <Button data-testid="controls-columns-done-btn" onClick={() => setPickerOpen(false)} style={{ backgroundColor: "var(--brand-primary)" }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="controls-save-view-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save this view</DialogTitle>
            <DialogDescription>Stores the visible columns, grouping and filters for this register.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="field-label">View Name</label>
              <Input data-testid="controls-view-name-input" value={saveForm.name} onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })} placeholder="e.g. Red risks by portfolio" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="controls-view-shared-checkbox" type="checkbox" className="h-4 w-4" checked={saveForm.shared} onChange={(e) => setSaveForm({ ...saveForm, shared: e.target.checked })} />
              <Users size={14} className="text-slate-400" /> Share with everyone in the workspace
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="controls-view-bi-checkbox" type="checkbox" className="h-4 w-4" checked={saveForm.bi_enabled} onChange={(e) => setSaveForm({ ...saveForm, bi_enabled: e.target.checked })} />
              <Database size={14} className="text-slate-400" /> Publish to BI (available as a Power BI / Tableau / Excel feed)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="controls-view-cancel-btn" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button data-testid="controls-view-save-btn" onClick={saveView} style={{ backgroundColor: "var(--brand-primary)" }}>Save View</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
